import React, { useEffect } from 'react'
import { useLocation, useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Box, Chip, CircularProgress, Typography } from '@mui/material'
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined'
import EventAvailableOutlinedIcon from '@mui/icons-material/EventAvailableOutlined'
import EventOutlinedIcon from '@mui/icons-material/EventOutlined'
import TouchAppOutlinedIcon from '@mui/icons-material/TouchAppOutlined'

import CustomTooltip from '../../components/CustomTooltip'
import PageHeader from '../../components/PageHeader'
import { SisSyncManager } from '../../APIManager'
import ROUTES from '../../consts/paths'
import PAGE_TITLES from '../../consts/titles'
import type { ApiError } from '../../models/Common'
import type { ISisSyncRun } from '../../models/SisSyncRun'
import type { CustomBreadcrumbT } from '../../utils/BreadcrumbDataPreparation'
import { notifyError } from '../../utils/notifyError'
import { formatRunTimestamp, getRunStatusChip, getRunTriggerDescription, getRunTriggerLabel } from '../../helpers/sisSyncRunDisplay'
import { getRunMeta, RUNNING_REFETCH_MS } from './runSummary'
import { SisSyncRunFilesTable } from './SisSyncRunFilesTable'

// The api interceptor rejects with { status, message }.
const isNotFound = (error: unknown): boolean => (error as Partial<ApiError> | null)?.status === 404

// An icon that stands in for a label, the same Event / EventAvailable pair the email schedule row
// uses for its two times. The label is its hover text and its accessible name, and it
// takes focus so keyboard users get the same hover.
const LabelledIcon = ({ label, icon }: { label: string, icon: React.ReactElement }): JSX.Element => (
  <CustomTooltip title={label}>
    <Box component="span" tabIndex={0} aria-label={label} role="img" display="inline-flex" color="text.secondary">
      {icon}
    </Box>
  </CustomTooltip>
)

const MetaLine = ({ run }: { run: ISisSyncRun }): JSX.Element => {
  const meta = getRunMeta(run)
  const parts: React.ReactElement[] = [
    <Box key="started" component="span" display="inline-flex" alignItems="center" gap={0.5}>
      <LabelledIcon label="Started" icon={<EventOutlinedIcon fontSize="small" />} />
      {meta.started}
    </Box>
  ]
  if (meta.progress != null) parts.push(<span key="progress">{meta.progress}</span>)
  if (meta.lastProgress != null) parts.push(<span key="lastProgress">{meta.lastProgress}</span>)
  if (meta.finished != null) {
    parts.push(
      <Box key="finished" component="span" display="inline-flex" alignItems="center" gap={0.5}>
        <LabelledIcon label="Finished" icon={<EventAvailableOutlinedIcon fontSize="small" />} />
        {meta.finished}
      </Box>
    )
  }
  if (meta.duration != null) parts.push(<span key="duration">{meta.duration}</span>)

  return (
    <Typography variant="body1" component="div" display="flex" flexWrap="wrap" alignItems="center" columnGap={1}>
      {parts.map((part, index) => (
        <React.Fragment key={part.key}>
          {index > 0 && <span aria-hidden="true">·</span>}
          {part}
        </React.Fragment>
      ))}
    </Typography>
  )
}

const TriggerBadge = ({ run }: { run: ISisSyncRun }): JSX.Element => (
  <CustomTooltip title={getRunTriggerDescription(run.trigger)}>
    <Chip
      size="small"
      variant="outlined"
      label={getRunTriggerLabel(run.trigger)}
      icon={run.trigger === 'ON_DEMAND' ? <TouchAppOutlinedIcon /> : <AccessTimeOutlinedIcon />}
    />
  </CustomTooltip>
)

export const SisSyncRunDetail = (): JSX.Element => {
  const { runId } = useParams()
  const location = useLocation()

  const { data: run, isLoading, isError, error } = useQuery({
    queryKey: ['sisSyncRun', runId],
    queryFn: async (): Promise<ISisSyncRun> => (await SisSyncManager.getSisSyncRun(runId ?? '')).data,
    enabled: runId != null,
    // Errors are shown on the page; a 404 is an answer, not a failure worth retrying or a toast.
    meta: { silent: true },
    retry: (failureCount, queryError) => !isNotFound(queryError) && failureCount < 1,
    refetchInterval: (query) => query.state.data?.outcome === 'RUNNING' ? RUNNING_REFETCH_MS : false
  })

  useEffect(() => {
    if (isError && !isNotFound(error)) notifyError(error, { fallbackMessage: 'Failed to load the SIS sync run. Please try again.' })
  }, [isError])

  const title = run != null ? `Run, ${formatRunTimestamp(run.startedOn)}` : 'Run'

  const breadcrumb: CustomBreadcrumbT[] = [
    // There is no Admin Settings page to link to.
    { title: 'Admin Settings', path: ROUTES.ADMIN_PATH, isInScope: false },
    // The bare list: it doesn't restore sort or page from the URL. The back button returns to the
    // exact list URL the user came from.
    { title: PAGE_TITLES.SIS_SYNC_LOGS, path: ROUTES.SIS_SYNC_LOGS },
    { title, path: location.pathname }
  ]

  if (run == null) {
    let body: React.ReactNode = <CircularProgress size={24} aria-label="Loading run" />
    if (isError) {
      body = (
        <Typography variant="body1">
          {isNotFound(error) ? 'This sync run was not found.' : 'This sync run could not be loaded.'}
        </Typography>
      )
    } else if (!isLoading) {
      body = null
    }
    return <PageHeader breadcrumb={breadcrumb} title={PAGE_TITLES.SIS_SYNC_RUN} showBackButton description={body} />
  }

  const statusChip = getRunStatusChip(run)

  return (
    <>
      <PageHeader
        breadcrumb={breadcrumb}
        title={title}
        showBackButton
        statusChip={
          <Box display="flex" alignItems="center" gap={1}>
            <Chip size="small" label={statusChip.label} color={statusChip.color} variant={statusChip.variant} />
            <TriggerBadge run={run} />
          </Box>
        }
        description={
          <Box display="flex" flexDirection="column" gap={1} marginBottom={3}>
            <MetaLine run={run} />
            {run.failureMessage != null && <Typography variant="body1">{run.failureMessage}</Typography>}
          </Box>
        }
      />
      <SisSyncRunFilesTable run={run} />
    </>
  )
}
